/**
 * Raid mode's slowmode floor (Console v2 §3.4, decision 9): while it runs,
 * public server text channels (and their threads) get at least
 * `raid.slowmode_s` seconds, and members with Manage Messages are exempt,
 * as the server enforces it (admin/raid/raid.service.ts). The composer's
 * slowmode bar shows the effective delay.
 */
import {
  ChannelType,
  Permissions,
  isThreadChannelType,
  type Channel,
  type ClientConfig,
} from '@neosocial/protocol';
import type { DataState } from '../store/reducer';
import { can, channelPermissions } from '../store/selectors';

const TEXT: readonly number[] = [ChannelType.GUILD_TEXT, ChannelType.GUILD_ANNOUNCEMENT];

/** The floor in seconds for this person in `ch` now, or 0. */
export function raidFloor(
  d: DataState,
  ch: Channel,
  raid: ClientConfig['raid'],
  now = Date.now(),
): number {
  if (!raid?.active || Date.parse(raid.ends_at) <= now || !ch.guild_id) return 0;
  const base = isThreadChannelType(ch.type) && ch.parent_id ? d.channels[ch.parent_id] : ch;
  if (!base || !TEXT.includes(base.type)) return 0;
  // public: the @everyone overwrite (its id is the server's) doesn't hide it
  const everyone = (base.permission_overwrites ?? []).find((o) => o.id === ch.guild_id);
  if (everyone && (BigInt(everyone.deny || '0') & Permissions.VIEW_CHANNEL) !== 0n) return 0;
  if (can(channelPermissions(d, ch), Permissions.MANAGE_MESSAGES)) return 0;
  return raid.slowmode_s;
}
